import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ProphetCard } from "./ProphetCard";
import { ProphetLightbox } from "./ProphetLightbox";
import { INITIAL_ACTIVE_ID } from "@/config/prophetsData";

const AUTOPLAY_MS = 6000;
const DRAG_THRESHOLD = 60; // px

export function ProphetsCarousel({ prophets, onActiveChange }) {
  const startIdx = Math.max(0, prophets.findIndex((p) => p.id === INITIAL_ACTIVE_ID));
  const [active, setActive] = useState(startIdx === -1 ? 0 : startIdx);
  const [paused, setPaused] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const containerRef = useRef(null);
  const activeCardRef = useRef(null);
  const dragRef = useRef({ startX: 0, dragging: false, moved: false });

  // True whenever the last pointer sequence moved far enough to be a drag.
  // Read by card onSelect handlers to suppress the click that the browser
  // still fires after a swipe on touch / mouse-drag devices.
  const wasDragRef = useRef(false);
  const DRAG_CLICK_SUPPRESS_PX = 6;

  // Report the settled active card to parents (title accent, etc.).
  // Fires only after the state has committed, i.e., not during partial drags.
  useEffect(() => {
    if (typeof onActiveChange === "function") {
      onActiveChange(prophets[active]);
    }
  }, [active, prophets, onActiveChange]);

  const wrap = useCallback(
    (idx) => (idx + prophets.length) % prophets.length,
    [prophets.length],
  );

  const go = useCallback(
    (delta) => setActive((cur) => wrap(cur + delta)),
    [wrap],
  );
  const goTo = useCallback((idx) => setActive(wrap(idx)), [wrap]);

  // Signed offset that respects the shortest path around the loop.
  const relativeOffset = (i) => {
    const half = prophets.length / 2;
    let diff = i - active;
    if (diff >  half) diff -= prophets.length;
    if (diff < -half) diff += prophets.length;
    return diff;
  };

  // Autoplay (respects reduced-motion + pause + hover + drag).
  useEffect(() => {
    if (paused) return undefined;
    if (typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }
    const t = setInterval(() => setActive((c) => wrap(c + 1)), AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [paused, wrap]);

  // Keyboard nav — arrow keys when the carousel region is focused/hovered.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowLeft")  { e.preventDefault(); go(-1); }
      if (e.key === "ArrowRight") { e.preventDefault(); go(+1); }
    };
    el.addEventListener("keydown", onKey);
    return () => el.removeEventListener("keydown", onKey);
  }, [go]);

  // Pointer / touch drag to switch cards.
  const onPointerDown = (e) => {
    dragRef.current = { startX: e.clientX, dragging: true, moved: false };
    wasDragRef.current = false;
    setPaused(true);
    // NOTE: we intentionally do NOT call setPointerCapture here — capturing
    // the pointer on the stage prevents subsequent click events from
    // reaching the individual card <button> elements, which we need for
    // the click-to-center behaviour. Drag still works fine because
    // pointerup fires on the stage as long as the pointer stays inside it.
  };
  const onPointerMove = (e) => {
    if (!dragRef.current.dragging) return;
    if (Math.abs(e.clientX - dragRef.current.startX) > DRAG_CLICK_SUPPRESS_PX) {
      dragRef.current.moved = true;
      wasDragRef.current = true;
    }
  };
  const onPointerUp = (e) => {
    if (!dragRef.current.dragging) return;
    const dx = e.clientX - dragRef.current.startX;
    dragRef.current.dragging = false;
    if (Math.abs(dx) > DRAG_THRESHOLD) go(dx < 0 ? +1 : -1);
    setPaused(false);
    // Reset the click-suppress flag on the next tick so the synthesized
    // `click` event that follows this pointerup is still swallowed.
    if (wasDragRef.current) {
      setTimeout(() => { wasDragRef.current = false; }, 0);
    }
  };

  // Card-click handler — reuses the same `goTo` used by dots. Side cards
  // move to the centre. Clicking the already-centered card opens the
  // full-screen lightbox. Phantom clicks that follow a swipe / mouse-drag
  // are suppressed in both branches.
  const onCardSelect = useCallback((idx) => {
    if (wasDragRef.current) return;
    if (idx === active) {
      setLightboxOpen(true);
      return;
    }
    goTo(idx);
  }, [active, goTo]);

  // Pause autoplay while the lightbox is open.
  useEffect(() => {
    if (lightboxOpen) setPaused(true);
    else setPaused(false);
  }, [lightboxOpen]);

  return (
    <div
      ref={containerRef}
      role="region"
      aria-label="Meet the Prophets carousel"
      tabIndex={0}
      data-testid="prophets-carousel"
      className="prophets-carousel relative w-full outline-none"
      style={{ "--prophet-accent-current": prophets[active]?.accent || "#e8edf7" }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Card stage */}
      <div
        className="prophets-carousel-stage"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <ul
          className="prophets-carousel-track relative w-full h-full m-0 p-0 list-none"
          aria-live="polite"
        >
          {prophets.map((p, i) => (
            <ProphetCard
              key={p.id}
              ref={i === active ? activeCardRef : null}
              prophet={p}
              index={i}
              offset={relativeOffset(i)}
              isActive={i === active}
              onSelect={() => onCardSelect(i)}
            />
          ))}
        </ul>
      </div>

      {/* Arrows */}
      <button
        type="button"
        aria-label="Previous prophet"
        data-testid="carousel-prev"
        onClick={() => go(-1)}
        className="prophets-carousel-arrow prophets-carousel-arrow--left"
      >
        <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
      </button>
      <button
        type="button"
        aria-label="Next prophet"
        data-testid="carousel-next"
        onClick={() => go(+1)}
        className="prophets-carousel-arrow prophets-carousel-arrow--right"
      >
        <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
      </button>

      {/* Pagination */}
      <div
        role="tablist"
        aria-label="Carousel pagination"
        data-testid="carousel-pagination"
        className="prophets-carousel-pagination"
      >
        {prophets.map((p, i) => (
          <button
            key={p.id}
            type="button"
            role="tab"
            aria-selected={i === active}
            aria-label={`Show prophet ${i + 1}${p.name ? " — " + p.name : ""}`}
            data-testid={`carousel-dot-${i}`}
            onClick={() => goTo(i)}
            className={`prophets-carousel-dot ${i === active ? "is-active" : ""}`}
          />
        ))}
      </div>

      <ProphetLightbox
        prophet={prophets[active]}
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        restoreFocusRef={activeCardRef}
      />
    </div>
  );
}
